<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
      <ComSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
        <ComTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
    <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="flex justify-between items-center mb-6">
        <!-- <h1 class="text-2xl font-bold text-[#035a76]">Staff Directory</h1> -->
        <div class="relative">
          <input 
            v-model="searchQuery"
            type="text" 
            placeholder="Search staff..."
            class="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#035a76] focus:border-[#035a76]"
          >
          <div class="absolute left-3 top-2.5 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
  
      <!-- Section Tabs -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-8">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[activeTab === tab.id ? 'border-[#76ba51] text-[#035a76]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm']"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>
  
      <!-- Staff Table -->
      <div class="bg-white shadow-md rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff ID</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="staff in filteredStaff" :key="staff.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ staff.id }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span class="text-blue-600 font-medium">{{ getInitials(staff.full_name) || getInitials(staff.applicant_name) }} </span>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">{{ staff.full_name || staff.applicant_name }}</div>
                      <div class="text-sm text-gray-500">{{ staff.email || staff.applicant_email }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ staff.position_title }}</div>
                  <div v-if="staff.section_name" class="text-sm text-gray-500">{{ staff.section_name }} <span v-if="staff.class_name">({{ staff.class_name }})</span></div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="relative">

                    <img
                      :src="staff.image_file ? `${baseUrl}/${staff.image_file}` : '/images/default-user.png'"
                      alt="Employee Profile"
                      class="w-20 h-20 rounded-full object-cover border-4 border-[#74bd54] cursor-pointer hover:opacity-80 transition"
                      @click="triggerFileInput(staff.id)"
                    />
                
                    <input
                      type="file"
                      accept="image/*"
                      :ref="el => setFileInputRef(el, staff.id)"
                      class="hidden"
                      @change="handleImageUpload($event, staff)"
                    />

                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
  
      <!-- Staff Details Modal -->
      <StaffDetailsModal 
        v-if="selectedStaff"
        :staff="selectedStaff"
        @close="selectedStaff = null"
      />
    </div>
      </main>
  </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted } from 'vue';
  import api from '@/services/api';
  import StaffDetailsModal from '../../components/hr/StaffDetailsModal.vue';
  import HrSidebar from '../../components/hr/HrSidebar.vue';
import HrTopbar from '../../components/hr/HrTopbar.vue';

import { useRouter } from 'vue-router';
import ComSidebar from '../../components/com/ComSidebar.vue';
import ComTopbar from '../../components/com/ComTopbar.vue';
const router = useRouter();

const isSidebarOpen = ref(true);
  const isDarkMode = ref(false);

  const staffs = ref([]);
  const fileInputs = ref({});

const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };

      const currentPageTitle = ref('Staff Pictures');

  const baseUrl = import.meta.env.VITE_BASE_URL;
  console.log('baseUrl:', baseUrl);

  const toggleDarkMode = () => {
    isDarkMode.value = !isDarkMode.value;
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  };
  
  const tabs = [
    { id: 'all', name: 'All Staff' },
    { id: 'nursery', name: 'Nursery' },
    { id: 'primary', name: 'Primary' },
    { id: 'secondary', name: 'Secondary' },
    { id: 'outsource', name: 'Outsourced' },
    { id: 'non-teaching', name: 'Non-Teaching' },
    { id: 'extracurricular', name: 'Extracurricular' }
  ];
  
  const activeTab = ref('all');
  const searchQuery = ref('');
  const selectedStaff = ref(null);

  const staffMembers = ref([]);

const fetchStaffMembers = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');

    console.log(response.data);

    staffMembers.value = response.data.data.map(item => ({
      id: item.applicant_id,
      applicant_name: item.applicant_name,
      applicant_email: item.applicant_email,
      full_name: item.full_name,
      email: item.email,
      position_title: item.position?.position_title,
      section_name: item.section?.section_name,
      class_name: item.class?.class_name,
      joining_date: item.contract?.joining_date,
      status: item.applicant_status,
      image_file: item.contract?.employee_image,
      type: item.employee_type 
    }));

  } catch (error) {
    console.error('Error fetching staff members:', error);
  }
};
  
  
  const filteredStaff = computed(() => {
    let filtered = staffMembers.value;
    
    // Filter by tab
 if (activeTab.value !== 'all') {
  filtered = filtered.filter(staff => {
    if (activeTab.value === 'nursery') return staff.section_name === 'Nursery';
    if (activeTab.value === 'primary') return staff.section_name === 'Primary';
    if (activeTab.value === 'secondary') return staff.section_name === 'Secondary';
    if (activeTab.value === 'outsource') return staff.type === 'outsource';
    if (activeTab.value === 'non-teaching') return staff.type === 'non-teaching';
    if (activeTab.value === 'extracurricular') return staff.type === 'extracurricular';
    return true;
  });
}
    
    // Filter by search query
   if (searchQuery.value) {
  const query = searchQuery.value.toLowerCase();
  filtered = filtered.filter(staff => {
    const name = (staff.full_name || staff.applicant_name || '').toLowerCase();
    const email = (staff.email || staff.applicant_email || '').toLowerCase();
    const position = (staff.position_title || '').toLowerCase();
    const id = String(staff.id).toLowerCase();

    return name.includes(query) || email.includes(query) || position.includes(query) || id.includes(query);
  });
}
    
    return filtered;
  });
  
  // const viewStaff = (id) => {
  //   selectedStaff.value = staffMembers.value.find(staff => staff.id === id);
  // };

const viewStaff = (id) => {
  console.log('Navigating to staff:', id); // 🔍 check if called
  router.push(`/staff/${id}`);
};
  
  const editStaff = (id) => {
    // Navigate to edit page or open edit modal
    console.log('Edit staff:', id);
  };
  
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  const getStatusClass = (status) => {
    switch(status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'on leave': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const setFileInputRef = (el, staffId) => {
  if (el) {
    fileInputs.value[staffId] = el;
  }
};

const triggerFileInput = (staffId) => {
  fileInputs.value[staffId]?.click();
};

const handleImageUpload = async (event, staff) => {
  const file = event.target.files[0];

  if (!file) return;

  const formData = new FormData();

  formData.append('applicant_id', staff.id);
  formData.append('image', file);

  try {
    const response = await api.post(
      'upload_employee_image.php',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    console.log(response.data);

    // refresh data after upload
    fetchStaffMembers();

  } catch (error) {
    console.error('Upload failed:', error);
  }
};

  onMounted(() => {
    fetchStaffMembers();
  });
  </script>